// ─────────────────────────────────────────────────────────────────────────────
// CIP Signal — Design tokens  ("Meridian" direction, indigo-led SaaS)
//
// Single source of truth for colour, gradient, radius, shadow and type used by
// the redesigned dashboard surfaces. Kept framework-agnostic (plain JS object)
// so it can be consumed from inline styles, and mirrored as CSS custom
// properties in theme.css for class-based components and dark mode.
// ─────────────────────────────────────────────────────────────────────────────

export const tokens = {
  color: {
    // Primary accent — the one interactive colour (indigo / SaaS)
    indigo:      '#25dbcd',
    indigo600:   '#0d9488',
    indigo400:   '#5eead4',
    indigo50:    '#e6faf8',

    // Structural darks
    ink:         '#0F172A',   // headings
    navy:        '#141B34',

    // Neutrals — cool slate, biased slightly toward indigo
    slate700:    '#334155',
    slate500:    '#64748B',   // secondary text
    slate400:    '#94A3B8',
    slate300:    '#CBD5E1',
    slate200:    '#E2E8F0',
    slate100:    '#F1F5F9',

    // Surfaces
    canvas:      '#F7F8FB',
    surface:     '#FFFFFF',
    surfaceAlt:  '#FBFBFE',
    border:      '#E6E9F0',

    // Brand highlight — used sparingly (not the accent)
    gold:        '#E8B923',
    goldSoft:    '#F1D677',

    // Semantic (compliance states) — separate from the accent
    success:     '#12A150',
    successBg:   '#E7F6EE',
    warning:     '#E8912D',
    warningBg:   '#FDF2E3',
    danger:      '#E5484D',
    dangerBg:    '#FCEBEC',
    info:        '#25dbcd',
    infoBg:      '#e6faf8',
  },

  gradient: {
    indigo: 'linear-gradient(135deg, #7bf7eb 0%, #54ebd9 50%, #2edcc9 100%)',
    navy:   'linear-gradient(120deg, #232C4E 0%, #0E1525 100%)',
    gold:   'linear-gradient(135deg, #F5D976 0%, #E0A82E 100%)',
    mesh:   'radial-gradient(900px 400px at 12% -10%, rgba(37,219,205,.14), transparent 60%), radial-gradient(700px 380px at 100% 0%, rgba(232,185,35,.14), transparent 55%)',
  },

  radius: { sm: '8px', md: '12px', lg: '16px', pill: '999px' },

  shadow: {
    sm: '0 1px 2px rgba(16,24,40,.04), 0 4px 12px rgba(16,24,40,.05)',
    md: '0 1px 3px rgba(16,24,40,.06), 0 10px 24px rgba(16,24,40,.08)',
    lg: '0 2px 4px rgba(16,24,40,.05), 0 20px 48px rgba(16,24,40,.10)',
  },

  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

// Semantic status keys: 'primary' | 'success' | 'warning' | 'danger'
export const STATUS = {
  primary: { fg: tokens.color.indigo,   bg: tokens.color.indigo50,  bar: tokens.gradient.indigo },
  success: { fg: tokens.color.success,  bg: tokens.color.successBg, bar: tokens.color.success },
  warning: { fg: '#B96C10',             bg: tokens.color.warningBg, bar: tokens.color.warning },
  danger:  { fg: tokens.color.danger,   bg: tokens.color.dangerBg,  bar: tokens.color.danger },
};

// Map an explicit colour (as sent by the backend on each summary row) to a semantic
// status key. This is the authoritative source — the remark text in the tables is
// coloured straight from `row.color`, so KPI tiles / distribution segments must derive
// from the same value to stay consistent. Returns null for unknown colours so callers
// can fall back to text inference.
const COLOR_TO_STATUS = {
  green:  'success',
  orange: 'warning',
  yellow: 'warning',
  amber:  'warning',
  gold:   'warning',
  red:    'danger',
  black:  'primary',
  grey:   'primary',
  gray:   'primary',
  // hex equivalents used across the codebase
  '#12a150': 'success',
  '#16a34a': 'success',
  '#e8912d': 'warning',
  '#b96c10': 'warning',
  '#e5484d': 'danger',
  '#dc2626': 'danger',
  '#0f172a': 'primary',
  '#25dbcd': 'primary',
  '#6366f1': 'primary',
};

export const statusKeyFromColor = (color) => {
  if (!color) return null;
  return COLOR_TO_STATUS[String(color).trim().toLowerCase()] || null;
};

// Resolve the semantic status key for a summary row: prefer the explicit colour the
// backend provides, then fall back to inferring it from the label text.
export const statusKeyFromRow = (row = {}) => {
  const label = row.description || row.remark || row.final_remark || row.business_unit || '';
  return statusKeyFromColor(row.color) || statusKeyFromText(label);
};

// Map a summary-row description to a semantic status key.
// Order matters: check the most specific / most severe cases first.
export const statusKeyFromText = (text = '') => {
  const d = String(text).toLowerCase();
  if (/total|grand total/.test(d)) return 'primary';
  if (/270\s*\+|overdue|expired|not available|inconsistent|missed|short/.test(d)) return 'danger';
  if (/pending|awaiting|240|to 270|less than|watch|due/.test(d)) return 'warning';
  if (/closed|cleared|realis|realiz|valid|consistent|received|complete|fulfilled|success/.test(d)) return 'success';
  return 'primary';
};

// Format a numeric-ish count for KPI display.
export const formatCount = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString('en-US');
};

export default tokens;
